// LineItemInsert.tsx (2026-10-18, Michael). "Upload insert or COA" on ONE
// print — a certificate of authenticity, a packing slip, any sheet for that
// piece. We print it and include it with that print, at no charge. Shown on
// cart / checkout lines and on each print in My orders → order details.
// Logged in only; the server decides who may use the cart or order (its
// owner, or an admin building a cart for a customer). PDF only, ≤ 25 MB,
// one per print — a new file replaces the print's earlier one.
// Backend: functions/api/order-inserts.ts.

import { InsertFile, InsertScope, UserApi } from "@/api/user.api";
import { $account } from "@/stores/account";
import { FileButton, Loader } from "@mantine/core";
import { useStore } from "@nanostores/react";
import { map } from "nanostores";
import { useEffect, useState } from "react";

const MAX_BYTES = 25 * 1024 * 1024;

// One list per cart/order, shared by all of its lines (one request, not one
// per print). `hidden` = the server said this cart can't take inserts
// (e.g. a shipping-only cart).
const $lists = map<Record<string, InsertFile[] | "hidden" | undefined>>({});
const loading = new Set<string>();

const keyOf = (scope: InsertScope) =>
  "cartId" in scope ? `cart:${scope.cartId}` : `order:${scope.orderId}`;

function load(scope: InsertScope, force = false) {
  const key = keyOf(scope);
  if (loading.has(key) || (!force && $lists.get()[key] !== undefined)) return;
  loading.add(key);
  UserApi.listInserts(scope)
    .then(({ data }) => $lists.setKey(key, data.files || []))
    .catch((e: any) => {
      if (e?.response?.status === 400) $lists.setKey(key, "hidden");
      else $lists.setKey(key, []);
    })
    .finally(() => loading.delete(key));
}

type Props = {
  cartId?: string;
  orderId?: string;
  lineItemId: string;
  locked?: boolean;
};

export default function LineItemInsert({
  cartId,
  orderId,
  lineItemId,
  locked,
}: Props) {
  const account = useStore($account);
  const lists = useStore($lists);
  const scope: InsertScope = cartId ? { cartId } : { orderId: orderId || "" };
  const key = keyOf(scope);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (account.isLoggedIn && (cartId || orderId))
      load(cartId ? { cartId } : { orderId: orderId || "" });
  }, [account.isLoggedIn, cartId, orderId]);

  if (!account.isLoggedIn || !lineItemId || (!cartId && !orderId)) return null;
  const list = lists[key];
  if (list === "hidden") return null;
  const mine = (list || []).filter(f => f.lineItemId === lineItemId);
  const file = mine[mine.length - 1];

  const upload = async (picked: File | null) => {
    setError("");
    setNote("");
    if (!picked) return;
    if (picked.type !== "application/pdf" && !/\.pdf$/i.test(picked.name)) {
      setError("PDF files only.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError("PDFs up to 25 MB.");
      return;
    }
    setBusy(true);
    try {
      const files = await UserApi.uploadInsert(scope, lineItemId, picked);
      $lists.setKey(key, files);
      if (orderId)
        setNote("Sent to the studio — we'll print it with this piece.");
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "The upload didn't finish."
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!cartId) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await UserApi.removeInsert(cartId, lineItemId);
      $lists.setKey(key, data.files || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not remove it.");
    } finally {
      setBusy(false);
    }
  };

  const picker = (label: string) => (
    <FileButton onChange={upload} accept="application/pdf">
      {props => (
        <button
          type="button"
          {...props}
          disabled={busy || locked}
          className="text-giclee underline disabled:opacity-50"
          title="A COA, packing slip or any sheet for this print — PDF, up to 25 MB. We print it and include it at no charge."
        >
          {label}
        </button>
      )}
    </FileButton>
  );

  return (
    <div className="flex flex-col gap-1 text-sm">
      {file ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-neutral-600">Insert / COA:</span>
          <a
            href={file.url}
            target="_blank"
            rel="noreferrer"
            className="max-w-[220px] truncate text-giclee underline"
          >
            {file.name}
          </a>
          {cartId && !locked && (
            <button
              type="button"
              className="text-neutral-500 hover:text-red-600"
              disabled={busy}
              onClick={remove}
              aria-label={`Remove ${file.name}`}
            >
              ×
            </button>
          )}
          {!locked && picker("Replace")}
        </div>
      ) : (
        !locked && picker("Upload insert or COA")
      )}
      {busy && <Loader size="xs" />}
      {note && <p className="text-green-700">{note}</p>}
      {error && <p className="text-red-600">{error}</p>}
    </div>
  );
}
